/**
 * Settings → Reference Data's lookup sections, in their groups (#27; moved out of ReferenceDataSettingsPage for M286
 * settings search, which indexes every section by its title and blurb and links to its anchor). Each section declares
 * exactly the columns its table has; LookupTableEditor renders only what they support.
 */
import type { LookupListHook, LookupMutationHook, LookupTableCapabilities } from './LookupTableEditor';

// Property-attribute lookups (FK from `properties`).
import {
  useLookupPropertyStatusesList,
  useCreateLookupPropertyStatuses,
  useUpdateLookupPropertyStatuses,
  useDeleteLookupPropertyStatuses,
} from '../../api/generated_hooks/generated-lookup_property_statuses-hooks';
import {
  useLookupBuildingClassesList,
  useCreateLookupBuildingClasses,
  useUpdateLookupBuildingClasses,
  useDeleteLookupBuildingClasses,
} from '../../api/generated_hooks/generated-lookup_building_classes-hooks';
import {
  useLookupParkingTypesList,
  useCreateLookupParkingTypes,
  useUpdateLookupParkingTypes,
  useDeleteLookupParkingTypes,
} from '../../api/generated_hooks/generated-lookup_parking_types-hooks';
import {
  useLookupConstructionPurposesList,
  useCreateLookupConstructionPurposes,
  useUpdateLookupConstructionPurposes,
  useDeleteLookupConstructionPurposes,
} from '../../api/generated_hooks/generated-lookup_construction_purposes-hooks';
import {
  useLookupPropertyRightsList,
  useCreateLookupPropertyRights,
  useUpdateLookupPropertyRights,
  useDeleteLookupPropertyRights,
} from '../../api/generated_hooks/generated-lookup_property_rights-hooks';

// Transaction / comp lookups (FK from sale/lease transactions + comp projects).
import {
  useLookupConditionsOfSaleList,
  useCreateLookupConditionsOfSale,
  useUpdateLookupConditionsOfSale,
  useDeleteLookupConditionsOfSale,
} from '../../api/generated_hooks/generated-lookup_conditions_of_sale-hooks';
import {
  useLookupTransactionTypesList,
  useCreateLookupTransactionTypes,
  useUpdateLookupTransactionTypes,
  useDeleteLookupTransactionTypes,
} from '../../api/generated_hooks/generated-lookup_transaction_types-hooks';
import {
  useLookupCompTypesList,
  useCreateLookupCompTypes,
  useUpdateLookupCompTypes,
  useDeleteLookupCompTypes,
} from '../../api/generated_hooks/generated-lookup_comp_types-hooks';
import {
  useLookupPartyRolesList,
  useCreateLookupPartyRoles,
  useUpdateLookupPartyRoles,
  useDeleteLookupPartyRoles,
} from '../../api/generated_hooks/generated-lookup_party_roles-hooks';
import {
  useLookupAssignmentTypesList,
  useCreateLookupAssignmentTypes,
  useUpdateLookupAssignmentTypes,
  useDeleteLookupAssignmentTypes,
} from '../../api/generated_hooks/generated-lookup_assignment_types-hooks';
import {
  useLookupAssignmentSidesList,
  useCreateLookupAssignmentSides,
  useUpdateLookupAssignmentSides,
  useDeleteLookupAssignmentSides,
} from '../../api/generated_hooks/generated-lookup_assignment_sides-hooks';

// Unified request-tracker statuses (F1) — statuses-as-data behind release_notes.status_id.
import {
  useLookupReleaseNoteStatusesList,
  useCreateLookupReleaseNoteStatuses,
  useUpdateLookupReleaseNoteStatuses,
  useDeleteLookupReleaseNoteStatuses,
} from '../../api/generated_hooks/generated-lookup_release_note_statuses-hooks';

// M67 — organization lists behind a user's Department / Business Entity picks.
import {
  useLookupDepartmentsList,
  useCreateLookupDepartments,
  useUpdateLookupDepartments,
  useDeleteLookupDepartments,
} from '../../api/generated_hooks/generated-lookup_departments-hooks';
import {
  useLookupBusinessEntitiesList,
  useCreateLookupBusinessEntities,
  useUpdateLookupBusinessEntities,
  useDeleteLookupBusinessEntities,
} from '../../api/generated_hooks/generated-lookup_business_entities-hooks';

// M68 — directory lists behind a user's Market / Office / Specialty picks and
// Designations links. No generated DELETE (noDeleteGeneratedTables): archive only.
import {
  useLookupMarketsList,
  useCreateLookupMarkets,
  useUpdateLookupMarkets,
} from '../../api/generated_hooks/generated-lookup_markets-hooks';
import {
  useLookupOfficesList,
  useCreateLookupOffices,
  useUpdateLookupOffices,
} from '../../api/generated_hooks/generated-lookup_offices-hooks';
import {
  useLookupSpecialtiesList,
  useCreateLookupSpecialties,
  useUpdateLookupSpecialties,
} from '../../api/generated_hooks/generated-lookup_specialties-hooks';
import {
  useLookupDesignationsList,
  useCreateLookupDesignations,
  useUpdateLookupDesignations,
} from '../../api/generated_hooks/generated-lookup_designations-hooks';

export interface LookupSection extends LookupTableCapabilities {
  key: string;
  /** DB table name (e.g. "lookup_property_statuses") — drives usage-awareness. */
  dbTable: string;
  /** Section heading. */
  title: string;
  /** Singular entity label used inside the editor. */
  entityLabel: string;
  /** Plural entity label for empty states. */
  entityLabelPlural: string;
  /** One-liner describing where the values surface. */
  blurb: string;
  useList: LookupListHook;
  useCreate: LookupMutationHook;
  useUpdate: LookupMutationHook;
  /** Absent for a lookup mounted without its DELETE: archive only, no hard delete. */
  useDelete?: LookupMutationHook;
  /** Route archive through the extensible confirm (only for archived tables). */
  confirmArchive?: boolean;
}

export interface LookupGroup {
  key: string;
  title: string;
  sections: LookupSection[];
}

export const REFERENCE_DATA_GROUPS: LookupGroup[] = [
  {
    key: 'property',
    title: 'Property attributes',
    sections: [
      {
        key: 'property_statuses',
        title: 'Property Statuses',
        entityLabel: 'property status',
        entityLabelPlural: 'property statuses',
        blurb: 'Lifecycle status shown on the property form and detail header.',
        dbTable: 'lookup_property_statuses',
        useList: useLookupPropertyStatusesList,
        useCreate: useCreateLookupPropertyStatuses,
        useUpdate: useUpdateLookupPropertyStatuses,
        useDelete: useDeleteLookupPropertyStatuses,
        hasSortOrder: true,
        hasArchived: true,
        hasDescription: true,
        confirmArchive: true,
      },
      {
        key: 'building_classes',
        title: 'Building Classes',
        entityLabel: 'building class',
        entityLabelPlural: 'building classes',
        blurb: 'Class rating (A / B / C, …) used on the property form and ingestion.',
        dbTable: 'lookup_building_classes',
        useList: useLookupBuildingClassesList,
        useCreate: useCreateLookupBuildingClasses,
        useUpdate: useUpdateLookupBuildingClasses,
        useDelete: useDeleteLookupBuildingClasses,
        hasSortOrder: true,
        hasArchived: true,
        hasDescription: true,
        confirmArchive: true,
      },
      {
        key: 'parking_types',
        title: 'Parking Types',
        entityLabel: 'parking type',
        entityLabelPlural: 'parking types',
        blurb: 'Parking classification used on the property form and ingestion.',
        dbTable: 'lookup_parking_types',
        useList: useLookupParkingTypesList,
        useCreate: useCreateLookupParkingTypes,
        useUpdate: useUpdateLookupParkingTypes,
        useDelete: useDeleteLookupParkingTypes,
        hasSortOrder: true,
        hasArchived: true,
        confirmArchive: true,
      },
      {
        key: 'construction_purposes',
        title: 'Construction Purposes',
        entityLabel: 'construction purpose',
        entityLabelPlural: 'construction purposes',
        blurb: 'Build intent (build-to-suit, spec, …) used on the property form.',
        dbTable: 'lookup_construction_purposes',
        useList: useLookupConstructionPurposesList,
        useCreate: useCreateLookupConstructionPurposes,
        useUpdate: useUpdateLookupConstructionPurposes,
        useDelete: useDeleteLookupConstructionPurposes,
        hasSortOrder: true,
        hasArchived: true,
        hasDescription: true,
        confirmArchive: true,
      },
      {
        key: 'property_rights',
        title: 'Property Rights',
        entityLabel: 'property right',
        entityLabelPlural: 'property rights',
        blurb: 'Ownership interest (fee simple, leasehold, …) on the property form.',
        dbTable: 'lookup_property_rights',
        useList: useLookupPropertyRightsList,
        useCreate: useCreateLookupPropertyRights,
        useUpdate: useUpdateLookupPropertyRights,
        useDelete: useDeleteLookupPropertyRights,
        hasSortOrder: true,
        hasArchived: true,
        hasDescription: true,
        confirmArchive: true,
      },
    ],
  },
  {
    key: 'transactions',
    title: 'Transactions & comps',
    sections: [
      {
        key: 'conditions_of_sale',
        title: 'Conditions of Sale',
        entityLabel: 'condition of sale',
        entityLabelPlural: 'conditions of sale',
        blurb: 'Sale-condition flags (arm’s length, distressed, …) on sale comps.',
        dbTable: 'lookup_conditions_of_sale',
        useList: useLookupConditionsOfSaleList,
        useCreate: useCreateLookupConditionsOfSale,
        useUpdate: useUpdateLookupConditionsOfSale,
        useDelete: useDeleteLookupConditionsOfSale,
        hasSortOrder: true,
        hasArchived: true,
        hasDescription: true,
        confirmArchive: true,
      },
      {
        key: 'transaction_types',
        title: 'Transaction Types',
        entityLabel: 'transaction type',
        entityLabelPlural: 'transaction types',
        blurb: 'Sale vs lease transaction classification used across comps.',
        dbTable: 'lookup_transaction_types',
        useList: useLookupTransactionTypesList,
        useCreate: useCreateLookupTransactionTypes,
        useUpdate: useUpdateLookupTransactionTypes,
        useDelete: useDeleteLookupTransactionTypes,
        hasSortOrder: true,
        hasArchived: true,
        hasDescription: true,
        confirmArchive: true,
      },
      {
        key: 'comp_types',
        title: 'Comp Types',
        entityLabel: 'comp type',
        entityLabelPlural: 'comp types',
        blurb: 'Comp-project classification used when grouping comparables.',
        dbTable: 'lookup_comp_types',
        useList: useLookupCompTypesList,
        useCreate: useCreateLookupCompTypes,
        useUpdate: useUpdateLookupCompTypes,
        useDelete: useDeleteLookupCompTypes,
        hasSortOrder: true,
        hasArchived: true,
        hasDescription: true,
        confirmArchive: true,
      },
      {
        key: 'party_roles',
        title: 'Party Roles',
        entityLabel: 'party role',
        entityLabelPlural: 'party roles',
        blurb: 'Role of a party on a transaction/contact (buyer, seller, broker…).',
        dbTable: 'lookup_party_roles',
        useList: useLookupPartyRolesList,
        useCreate: useCreateLookupPartyRoles,
        useUpdate: useUpdateLookupPartyRoles,
        useDelete: useDeleteLookupPartyRoles,
        hasDescription: true,
      },
      {
        key: 'assignment_types',
        title: 'Assignment Types',
        entityLabel: 'assignment type',
        entityLabelPlural: 'assignment types',
        blurb: 'Type of property assignment (listing, tenant rep, …).',
        dbTable: 'lookup_assignment_types',
        useList: useLookupAssignmentTypesList,
        useCreate: useCreateLookupAssignmentTypes,
        useUpdate: useUpdateLookupAssignmentTypes,
        useDelete: useDeleteLookupAssignmentTypes,
        hasDescription: true,
      },
      {
        key: 'assignment_sides',
        title: 'Assignment Sides',
        entityLabel: 'assignment side',
        entityLabelPlural: 'assignment sides',
        blurb: 'Which side of a deal an assignment represents (landlord / tenant).',
        dbTable: 'lookup_assignment_sides',
        useList: useLookupAssignmentSidesList,
        useCreate: useCreateLookupAssignmentSides,
        useUpdate: useUpdateLookupAssignmentSides,
        useDelete: useDeleteLookupAssignmentSides,
        hasDescription: true,
      },
    ],
  },
  {
    key: 'organization',
    title: 'Organization',
    sections: [
      {
        key: 'departments',
        title: 'Departments',
        entityLabel: 'department',
        entityLabelPlural: 'departments',
        blurb: 'The department choices on a user (Manage Users). Pick-list only; no free text.',
        dbTable: 'lookup_departments',
        useList: useLookupDepartmentsList,
        useCreate: useCreateLookupDepartments,
        useUpdate: useUpdateLookupDepartments,
        useDelete: useDeleteLookupDepartments,
        hasSortOrder: true,
        hasArchived: true,
        codeRequired: true,
        confirmArchive: true,
      },
      {
        key: 'business_entities',
        title: 'Business Entities',
        entityLabel: 'business entity',
        entityLabelPlural: 'business entities',
        blurb: 'The business-entity choices on a user (Manage Users). Pick-list only; no free text.',
        dbTable: 'lookup_business_entities',
        useList: useLookupBusinessEntitiesList,
        useCreate: useCreateLookupBusinessEntities,
        useUpdate: useUpdateLookupBusinessEntities,
        useDelete: useDeleteLookupBusinessEntities,
        hasSortOrder: true,
        hasArchived: true,
        codeRequired: true,
        confirmArchive: true,
      },
      {
        key: 'markets',
        title: 'Markets',
        entityLabel: 'market',
        entityLabelPlural: 'markets',
        blurb: 'The market a person works in (Manage Users → Directory fields, and the directory filters).',
        dbTable: 'lookup_markets',
        useList: useLookupMarketsList,
        useCreate: useCreateLookupMarkets,
        useUpdate: useUpdateLookupMarkets,
        hasSortOrder: true,
        hasArchived: true,
        codeRequired: true,
        confirmArchive: true,
      },
      {
        key: 'offices',
        title: 'Offices',
        entityLabel: 'office',
        entityLabelPlural: 'offices',
        blurb: 'The office a person sits in, including Virtual and Remote (Manage Users → Directory fields).',
        dbTable: 'lookup_offices',
        useList: useLookupOfficesList,
        useCreate: useCreateLookupOffices,
        useUpdate: useUpdateLookupOffices,
        hasSortOrder: true,
        hasArchived: true,
        codeRequired: true,
        confirmArchive: true,
      },
      {
        key: 'specialties',
        title: 'Specialties',
        entityLabel: 'specialty',
        entityLabelPlural: 'specialties',
        blurb: 'A person’s one specialty, shown in the directory (Manage Users → Directory fields).',
        dbTable: 'lookup_specialties',
        useList: useLookupSpecialtiesList,
        useCreate: useCreateLookupSpecialties,
        useUpdate: useUpdateLookupSpecialties,
        hasSortOrder: true,
        hasArchived: true,
        codeRequired: true,
        confirmArchive: true,
      },
      {
        key: 'designations',
        title: 'Designations & Memberships',
        entityLabel: 'designation',
        entityLabelPlural: 'designations',
        blurb: 'Letters after a name (CCIM, SIOR) and organisations (NAIOP); a person can hold several.',
        dbTable: 'lookup_designations',
        useList: useLookupDesignationsList,
        useCreate: useCreateLookupDesignations,
        useUpdate: useUpdateLookupDesignations,
        hasSortOrder: true,
        hasArchived: true,
        codeRequired: true,
        confirmArchive: true,
        choiceField: {
          key: 'kind',
          label: 'Kind',
          options: [
            { value: 'designation', label: 'Designation' },
            { value: 'membership', label: 'Membership' },
          ],
          defaultValue: 'designation',
        },
      },
    ],
  },
  {
    key: 'request_tracker',
    title: 'Request tracker',
    sections: [
      {
        key: 'release_note_statuses',
        title: 'Request / Release-note Statuses',
        entityLabel: 'status',
        entityLabelPlural: 'statuses',
        blurb: 'Unified request-tracker statuses shown on release notes and client requests. Behavior flags (settable-by, visibility, reopen) are seeded and edited via migration for now.',
        dbTable: 'lookup_release_note_statuses',
        useList: useLookupReleaseNoteStatusesList,
        useCreate: useCreateLookupReleaseNoteStatuses,
        useUpdate: useUpdateLookupReleaseNoteStatuses,
        useDelete: useDeleteLookupReleaseNoteStatuses,
        hasSortOrder: true,
        hasArchived: true,
        hasDescription: true,
        codeRequired: true,
        confirmArchive: true,
      },
    ],
  },
];

/** A section's anchor: /settings/data-curation#reference-section-<key> opens it and scrolls to it (M286: /users and settings search link here). */
export const REFERENCE_SECTION_HASH = '#reference-section-';
